import React, { useEffect, useRef, useState } from 'react';
import { Modal, Spin, Empty, Card, Tooltip } from 'antd';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { MakeApiCall } from '../../../../../apis';
import { Wrapper } from '../style';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Format with date and time
const formatDateTime = (ts) =>
  new Date(ts * 1000).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

// Format just date for x-axis (to avoid crowding)
const formatDateForXAxis = (ts) =>
  new Date(ts * 1000).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short'
  });

// Format with full date and time for tooltip
const formatTooltipDateTime = (ts) =>
  new Date(ts * 1000).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

// Fetch all records
const fetchAllPages = async (asin, startDate, endDate, token) => {
  const res = await MakeApiCall(
    'products/sales-rank-history?no_pagination=true',
    'post',
    { asin, start_date: startDate, end_date: endDate },
    true,
    {},
    token
  );
  if (!res?.status) throw new Error(res?.message || 'Failed to load BSR history');
  return res.data?.records || [];
};

// Process records: Keep all records with their timestamps
const processRecords = (records) => {
  if (!records.length) return {
    timestamps: [],
    subData: [],
    rootData: [],
    subLabel: '',
    rootLabel: '',
    rawRecords: []
  };

  // Sort by created_at (oldest to newest)
  const sorted = [...records].sort((a, b) => a.created_at - b.created_at);

  // For chart - show each record as a point (not grouping by day)
  const timestamps = sorted.map(r => r.created_at);
  const subData = sorted.map(r => r.subcategory_sales_rank);
  const rootData = sorted.map(r => r.root_category_sales_rank);
  const xAxisLabels = sorted.map(r => formatDateForXAxis(r.created_at));

  // Get category titles
  const subLabel = records[0]?.subcategory_title || 'Subcategory';
  const rootLabel = records[0]?.root_category_title || 'Root Category';

  return {
    timestamps,
    subData,
    rootData,
    xAxisLabels,
    subLabel,
    rootLabel,
    rawRecords: sorted
  };
};

const SalesRankModal = ({ open, onClose, asin, productTitle, startDate, endDate, token }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartInfo, setChartInfo] = useState(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!open || !asin) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      setChartInfo(null);
      try {
        const records = await fetchAllPages(asin, startDate, endDate, token);

        if (!records.length) {
          setChartInfo({
            timestamps: [],
            subData: [],
            rootData: [],
            xAxisLabels: [],
            subLabel: '',
            rootLabel: '',
            rawRecords: []
          });
          return;
        }

        const processed = processRecords(records);
        setChartInfo(processed);
      } catch (e) {
        setError(e.message || 'Failed to load BSR history');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, asin, startDate, endDate, token]);

  const hasData = chartInfo && chartInfo.timestamps && chartInfo.timestamps.length > 0;

  const subColor = '#314F71';
  const rootColor = '#f45b68';

  // Prepare chart data for Chart.js
  const chartData = {
    labels: hasData ? chartInfo.xAxisLabels : [],
    datasets: [
      {
        label: chartInfo?.subLabel || 'Subcategory',
        data: hasData ? chartInfo.subData : [],
        borderColor: subColor,
        backgroundColor: 'transparent',
        borderWidth: 2.5,
        tension: 0.4, // smooth curve
        pointRadius: 0,
        pointHoverRadius: 4,
        pointBackgroundColor: subColor,
        pointBorderColor: '#fff',
        pointBorderWidth: 0,
        yAxisID: 'y',
      },
      {
        label: chartInfo?.rootLabel || 'Root Category',
        data: hasData ? chartInfo.rootData : [],
        borderColor: rootColor,
        backgroundColor: 'transparent',
        borderWidth: 2.5,
        tension: 0.4, // smooth curve
        pointRadius: 0,
        pointHoverRadius: 4,
        pointBackgroundColor: rootColor,
        pointBorderColor: '#fff',
        pointBorderWidth: 0,
        yAxisID: 'y1',
      }
    ]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            if (chartInfo && chartInfo.timestamps && chartInfo.timestamps[index]) {
              return formatTooltipDateTime(chartInfo.timestamps[index]);
            }
            return tooltipItems[0].label;
          },
          label: (context) => {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            const value = context.parsed.y;
            if (value === null || value === undefined) {
              label += '—';
            } else {
              label += `#${Math.round(value).toLocaleString()}`;
            }
            return label;
          }
        },
        backgroundColor: 'white',
        titleColor: '#333',
        bodyColor: '#666',
        borderColor: '#ddd',
        borderWidth: 1,
        padding: 10,
        displayColors: true,
      },
      legend: {
        position: 'top',
        align: 'center',
        labels: {
          font: {
            size: 12,
          },
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
          padding: 12,
        }
      },
    },
    scales: {
      x: {
        title: {
          display: false,
          text: 'Date & Time',
        },
        ticks: {
          font: {
            size: 12,
          },
          color: '#888',
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 10,
        },
        grid: {
          display: false,
        },
        border: {
          display: false,
        }
      },
      y: {
        position: 'left',
        title: {
          display: false,
          text: chartInfo?.subLabel || 'Subcategory Rank',
          color: subColor,
          font: {
            size: 12,
            weight: '500',
          }
        },
        ticks: {
          callback: (value) => {
            if (value === null || value === undefined) return '—';
            return `#${Math.round(value).toLocaleString()}`;
          },
          font: {
            size: 12,
          },
          color: subColor,
        },
        grid: {
          color: '#f0f0f0',
          drawBorder: false,
          lineWidth: 1,
          tickWidth: 0,
        },
        reverse: false, // Not reversing as per original (commented out)
      },
      y1: {
        position: 'right',
        title: {
          display: false,
          text: chartInfo?.rootLabel || 'Root Category Rank',
          color: rootColor,
          font: {
            size: 12,
            weight: '500',
          }
        },
        ticks: {
          callback: (value) => {
            if (value === null || value === undefined) return '—';
            return `#${Math.round(value).toLocaleString()}`;
          },
          font: {
            size: 12,
          },
          color: rootColor,
        },
        grid: {
          display: false,
        },
      }
    },
    elements: {
      line: {
        borderJoin: 'round',
        borderCap: 'round',
      },
      point: {
        hoverRadius: 5,
        radius: 0,
      }
    },
    layout: {
      padding: {
        top: 10,
        bottom: 0,
        left: 5,
        right: 5
      }
    }
  };

  // Calculate statistics
  const latestSub = hasData ? chartInfo.subData[chartInfo.subData.length - 1] : null;
  const latestRoot = hasData ? chartInfo.rootData[chartInfo.rootData.length - 1] : null;
  const bestSub = hasData ? Math.min(...chartInfo.subData.filter(v => v != null)) : null;
  const bestRoot = hasData ? Math.min(...chartInfo.rootData.filter(v => v != null)) : null;
  const worstSub = hasData ? Math.max(...chartInfo.subData.filter(v => v != null)) : null;
  const worstRoot = hasData ? Math.max(...chartInfo.rootData.filter(v => v != null)) : null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1100}
      title={
        <div className="d-flex flex-column gap-1">
          <span className="fw-bold fs-4 mb-2 text-gray-900">BSR Trend Analysis</span>
          <div className="fs-8 d-flex flex-wrap gap-2">
            <span><b>ASIN:</b> {asin}</span>
            {startDate && endDate && (
              <span className="ms-3"><i className='ki-outline ki-filter-search text-danger fs-6'></i> Filter: {startDate} → {endDate}</span>
            )}
            <div>
            <Tooltip title="Change date range from the main dashboard.">
                  <i className="ki-outline ki-information-2 fs-5 text-muted" style={{ cursor: 'pointer' }}></i>
            </Tooltip>
          </div>
          </div>
          
        </div>
      }
      destroyOnClose
      centered
    >
      <Wrapper>
        {/* Stats Cards */}
        {hasData && (
          <div className="d-flex gap-3 mb-3 flex-wrap">
            {/* Subcategory Card */}
            <Card
              style={{
                flex: 1, minWidth: 180, padding: '8px 12px',
                borderRadius: 8, background: '#EEF3FA',
                borderLeft: `3px solid ${subColor}`,
              }}
              bodyStyle={{ padding: 0 }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="fs-7 text-muted mb-1">{chartInfo.subLabel}</div>
                  <div className="fw-bold fs-2" style={{ color: subColor, lineHeight: 1.2 }}>
                    #{latestSub?.toLocaleString()}
                  </div>
                </div>
                <div className="text-end">
                  <div className="fs-5">
                    <div className='d-flex align-items-center gap-1'>
                      <i className='ki-outline ki-cup fs-5 text-success'></i>
                      <span className="text-success">{bestSub?.toLocaleString()}</span>
                      <span className="text-muted mx-1">/</span>
                      <i className='ki-outline ki-graph-up fs-5 text-danger'></i>
                      <span className="text-danger">{worstSub?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                
                {/* <i></i> */}

              </div>
            </Card>

            {/* Root Category Card */}
            <Card style={{
              flex: 1, minWidth: 180, padding: '8px 12px',
              borderRadius: 8, background: '#FEF0EB',
              borderLeft: `3px solid ${rootColor}`,
            }}
              bodyStyle={{ padding: 0 }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="fs-7 text-muted mb-1">{chartInfo.rootLabel}</div>
                  <div className="fw-bold fs-2" style={{ color: rootColor, lineHeight: 1.2 }}>
                    #{latestRoot?.toLocaleString()}
                  </div>
                </div>
                <div className="text-end">
                  <div className='d-flex align-items-center gap-1'>
                    <i className='ki-outline ki-cup fs-5 text-success'></i>
                    <span className="text-success">{bestRoot?.toLocaleString()}</span>
                    <span className="text-muted mx-1">/</span>
                    <i className='ki-outline ki-graph-up fs-5 text-danger'></i>
                    <span className="text-danger">{worstRoot?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Summary Card */}
            <Card style={{
              flex: 0.7, minWidth: 140, padding: '8px 12px',
              borderRadius: 8, background: '#F4F6F9',
              borderLeft: '3px solid #adb5bd',
            }}
              bodyStyle={{ padding: 0 }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="fs-7 text-muted mb-1">Data Points</div>
                  <div className="fw-bold fs-2 text-gray-700" style={{ lineHeight: 1.2 }}>
                    {chartInfo.timestamps.length}
                  </div>
                </div>
                <div className="text-end fs-6 text-muted d-flex align-items-center gap-1">
                  <i className='ki-outline ki-graph-up fs-5 text-danger'></i> <div> Real-time</div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Chart */}
        <div style={{ minHeight: 400 }}>
          {loading && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
              <Spin size="large" tip="Loading BSR history..." />
            </div>
          )}

          {!loading && error && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
              <Empty description={error} />
            </div>
          )}

          {!loading && !error && chartInfo && chartInfo.timestamps.length === 0 && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
              <Empty description="No BSR data available for the selected period" />
            </div>
          )}

          {!loading && !error && hasData && (
            <Line
              ref={chartRef}
              data={chartData}
              options={chartOptions}
              height={100}
            />
          )}
        </div>
          <div className='separator border-gray-300 separator-dashed'></div>
        {/* Data Table with Date and Time */}
        {hasData && (
          <div className="mt-4">
            <details>
              <summary className="text-gray-800 fs-6" style={{ cursor: 'pointer' }}>
                📋 View detailed data with date and time
              </summary>
              <div style={{
                maxHeight: '300px',
                overflow: 'auto',
                marginTop: '12px',
                border: '1px solid #f0f0f0',
                borderRadius: '8px'
              }}>
                <table className="table table-sm table-hover" style={{ fontSize: '12px' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#fafafa' }}>
                    <tr>
                      <th>#</th>
                      <th>Date & Time</th>
                      <th>Subcategory Rank</th>
                      <th>Root Category Rank</th>
                      <th>Subcategory Title</th>
                      <th>Root Category Title</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartInfo.rawRecords.map((record, idx) => (
                      <tr key={record.id}>
                        <td>{idx + 1}</td>
                        <td>{formatDateTime(record.created_at)}</td>
                        <td>
                          <span className="fw-semibold" style={{ color: subColor }}>
                            #{record.subcategory_sales_rank?.toLocaleString()}
                          </span>
                        </td>
                        <td>
                          <span className="fw-semibold" style={{ color: rootColor }}>
                            #{record.root_category_sales_rank?.toLocaleString()}
                          </span>
                        </td>
                        <td>{record.subcategory_title}</td>
                        <td>{record.root_category_title}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </div>
        )}
      </Wrapper>
    </Modal>
  );
};

export default SalesRankModal;